import React, { useState, useRef, useCallback } from "react";
import { FaCloudUploadAlt } from "react-icons/fa";
import { ref, getDownloadURL, uploadBytes } from "firebase/storage";
import { addDoc, collection, doc, setDoc, Timestamp } from "firebase/firestore";
import { storage, db, auth } from "../firebaseConfig";
import { useNavigate } from "react-router-dom";

// Google Maps imports
import { GoogleMap, Marker, Autocomplete } from "@react-google-maps/api";

const categories = [
  "Arts & Culture",
  "Education & Skills",
  "Entertainment",
  "Family & Community",
  "Online & Virtual",
  "Pets & Animals",
  "Sports & Fitness",
  "Travel & Outdoor",
];

const genders = ["Anyone", "Male", "Female"];

// Функция для сжатия изображения (пример из предыдущего ответа)
function resizeImageFile(file, maxSize = 500) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxSize) {
            height = Math.round((height *= maxSize / width));
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = Math.round((width *= maxSize / height));
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              return reject(new Error("Canvas is empty"));
            }
            const newFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            resolve(newFile);
          },
          file.type,
          0.9
        );
      };
      img.onerror = (err) => reject(err);
      img.src = e.target.result;
    };
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(file);
  });
}

const NewEvent = () => {
  const navigate = useNavigate();

  const [values, setValues] = useState({
    images: [],
    title: "",
    category: "",
    gender: "",
    contact: "",
    description: "",
    location: "",
    lat: null,
    lng: null,
    eventDate: "",
    error: "",
    loading: false,
  });

  const {
    images,
    title,
    category,
    gender,
    contact,
    description,
    location,
    lat,
    lng,
    eventDate,
    error,
    loading,
  } = values;

  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Обработка изменения в простых полях
  const handleChange = (e) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Обработка выбора адреса через Autocomplete
  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.geometry) {
        const newLat = place.geometry.location.lat();
        const newLng = place.geometry.location.lng();
        const newAddress = place.formatted_address;

        setValues((prev) => ({
          ...prev,
          location: newAddress,
          lat: newLat,
          lng: newLng,
        }));

        if (mapRef.current) {
          mapRef.current.panTo({ lat: newLat, lng: newLng });
        }
      }
    }
  };

  // Клик по карте
  const handleMapClick = (event) => {
    const newLat = event.latLng.lat();
    const newLng = event.latLng.lng();

    setValues((prev) => ({
      ...prev,
      lat: newLat,
      lng: newLng,
    }));
  };

  // Сохранение ссылки на карту
  const onLoadMap = useCallback((map) => {
    mapRef.current = map;
  }, []);

  // Обработчик выбора файлов (изображений)
  const handleImageChange = async (e) => {
    try {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      const resizedFiles = [];
      for (let file of files) {
        const resizedFile = await resizeImageFile(file, 500);
        resizedFiles.push(resizedFile);
      }

      setValues((prev) => ({ ...prev, images: resizedFiles }));
    } catch (err) {
      console.error("Error while resizing:", err);
      setValues((prev) => ({ ...prev, error: "Error while resizing images" }));
    }
  };

  // Обработка отправки формы
  const handleSubmit = async (e) => {
    e.preventDefault();
    setValues((prev) => ({ ...prev, error: "", loading: true }));

    try {
      // 1. Загружаем картинки в Firebase
      let imgs = [];

      if (images.length) {
        for (let image of images) {
          const imgRef = ref(storage, `ads/${Date.now()} - ${image.name}`);
          const result = await uploadBytes(imgRef, image);
          const fileUrl = await getDownloadURL(ref(storage, result.ref.fullPath));
          imgs.push({ url: fileUrl, path: result.ref.fullPath });
        }
      } else {
        // Если пользователь не загрузил картинок, используем default
        const defaultRef = ref(storage, "ads/default.jpeg");
        const defaultUrl = await getDownloadURL(defaultRef);
        imgs.push({ url: defaultUrl, path: defaultRef.fullPath });
      }

      // 2. Создаём документ в Firestore
      const docRef = await addDoc(collection(db, "ads"), {
        images: imgs,
        title,
        category,
        gender,
        contact,
        description,
        location,
        lat,
        lng,
        eventDate: eventDate ? Timestamp.fromDate(new Date(eventDate)) : null,
        isPending: false,
        publishedAt: Timestamp.fromDate(new Date()),
        postedBy: auth.currentUser.uid,
      });

      // 3. Обновляем документ, добавляем adId
      await setDoc(
        doc(db, "ads", docRef.id),
        { adId: docRef.id },
        { merge: true }
      );

      // 4. Создаём "favorites" (по необходимости)
      await setDoc(doc(db, "favorites", docRef.id), {
        users: [],
      });

      // 5. Сбрасываем форму
      setValues({
        images: [],
        title: "",
        category: "",
        gender: "",
        contact: "",
        description: "",
        location: "",
        lat: null,
        lng: null,
        eventDate: "",
        error: "",
        loading: false,
      });

      navigate("/");
    } catch (err) {
      setValues((prev) => ({ ...prev, error: err.message, loading: false }));
    }
  };

  return (
    <form className="form shadow rounded p-3 mt-5" onSubmit={handleSubmit}>
      <h3 className="text-center mb-3">Create a new event</h3>

      {/* Загрузка изображения */}
      <div className="mb-3 text-center">
        <label htmlFor="image">
          <div className="btn btn-secondary btn-sm">
            <FaCloudUploadAlt size={30} /> Upload Image
          </div>
        </label>
        <input
          type="file"
          id="image"
          style={{ display: "none" }}
          accept="image/*"
          multiple
          onChange={handleImageChange}
        />
      </div>

      {/* Поле Title (макс. 70 символов) */}
      <div className="mb-3">
        <label className="form-label">
          Title <span style={{ color: "red" }}>*</span>
        </label>
        <input
          type="text"
          className="form-control"
          name="title"
          value={title}
          onChange={handleChange}
          maxLength={70} // ограничение символов
          required
        />
      </div>

      {/* Категория */}
      <div className="mb-3">
        <label className="form-label">
          Category <span style={{ color: "red" }}>*</span>
        </label>
        <select
          name="category"
          className="form-select"
          onChange={handleChange}
          value={category}
          required
        >
          <option value="">Select</option>
          {categories.map((cat) => (
            <option value={cat} key={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {/* Gender */}
      <div className="mb-3">
        <label className="form-label">
          I'm looking for <span style={{ color: "red" }}>*</span>
        </label>
        <select
          name="gender"
          className="form-select"
          onChange={handleChange}
          value={gender}
          required
        >
          <option value="">Select</option>
          {genders.map((g) => (
            <option value={g} key={g}>
              {g}
            </option>
          ))}
        </select>
      </div>

      {/* Дата мероприятия */}
      <div className="mb-3">
        <label className="form-label">
          Event Date <span style={{ color: "red" }}>*</span>
        </label>
        <input
          type="date"
          className="form-control"
          name="eventDate"
          value={eventDate}
          onChange={handleChange}
          required
        />
      </div>

      {/* Контакт (макс. 70 символов) */}
      <div className="mb-3">
        <label className="form-label">Your contact (email/phone number)</label>
        <input
          type="text"
          className="form-control"
          name="contact"
          value={contact}
          onChange={handleChange}
          maxLength={70} // ограничение символов
        />
      </div>

      {/* Описание (макс. 500 символов) */}
      <div className="mb-3">
        <label className="form-label">
          Description <span style={{ color: "red" }}>*</span>
        </label>
        <textarea
          name="description"
          cols="30"
          rows="3"
          className="form-control"
          value={description}
          onChange={handleChange}
          maxLength={500} // ограничение символов
          required
        />
      </div>

      {/* Локация (макс. 70 символов) */}
      <div className="mb-3">
        <label className="form-label">Meeting location</label>
        <Autocomplete
          onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
          onPlaceChanged={handlePlaceChanged}
        >
          <input
            type="text"
            className="form-control"
            placeholder="Enter event location..."
            value={location}
            onChange={(e) => setValues({ ...values, location: e.target.value })}
            maxLength={70} // ограничение символов
          />
        </Autocomplete>
      </div>

      {/* Карта */}
      <div style={{ width: "100%", height: "400px" }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={{
            lat: lat || 37.7749,
            lng: lng || -122.4194,
          }}
          zoom={lat && lng ? 14 : 12}
          onLoad={onLoadMap}
          onClick={handleMapClick}
        >
          {lat && lng && <Marker position={{ lat, lng }} />}
        </GoogleMap>
      </div>

      {error ? <p className="text-center text-danger">{error}</p> : null}

      <div className="mb-3 text-center" style={{ marginTop: "1rem" }}>
        <button className="btn btn-secondary btn-sm" disabled={loading}>
          Create
        </button>
      </div>
    </form>
  );
};

export default NewEvent;
